import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
} from "@mui/material";
import {
  X,
  Edit,
  ArrowLeft,
  Calendar,
  Clock,
  FileText,
  User2,
  Briefcase,
  Building2,
  Image as ImageIcon,
  Save,
  PackageCheck,
  Copy,
  Share2,
  Printer,
  MoreVertical,
  Bell,
  Calculator,
  CheckCircle2,
  Trash2,
  ChevronRight,
  StickyNote,
  Upload,
  CornerUpLeft,
} from "lucide-react";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const ViewJobslip = () => {
  const [showReceiveHistoryModal, setShowReceiveHistoryModal] = useState(false);
  const [showTransferHistoryModal, setShowTransferHistoryModal] =
    useState(false);
  const navigate = useNavigate();
  const { id } = useParams();
  const [isEditMode, setIsEditMode] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [showBillModal, setShowBillModal] = useState(false);
  const [isBillViewMode, setIsBillViewMode] = useState(false);
  const [showDeleteBillDialog, setShowDeleteBillDialog] = useState(false);
  const [billDetails, setBillDetails] = useState({
    billPhotos: [],
    billNo: "",
    billerName: "",
    jobRate: "",
    billDate: "",
    billNote: "",
  });
  const [isSubmittingBill, setIsSubmittingBill] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notes, setNotes] = useState("");
  const [isSubmittingNotes, setIsSubmittingNotes] = useState(false);
  const [isNotesViewMode, setIsNotesViewMode] = useState(false);
  const [showSettleMaterialsModal, setShowSettleMaterialsModal] =
    useState(false);
  const [settleForm, setSettleForm] = useState({
    settleReason: "",
    settleDateTime: dayjs(),
    settledBy: "Thomas", // Non-editable
    settleChallanNo: "",
    settleNote: "",
  });
  const [isSubmittingSettle, setIsSubmittingSettle] = useState(false);
  const [settleQuantities, setSettleQuantities] = useState({});
  const [reminderModalOpen, setReminderModalOpen] = useState(false);
  const [reminderDateTime, setReminderDateTime] = useState(null);
  const [reminderDescription, setReminderDescription] = useState("");
  const [isReminderViewMode, setIsReminderViewMode] = useState(false);
  const [selectedReminder, setSelectedReminder] = useState(null);

  // Sample reminder data - In real app, this would come from an API
  const [reminders, setReminders] = useState([
    {
      id: 1,
      dateTime: dayjs().add(2, "day"),
      description: "Follow up on material quality",
      status: "pending", // pending, completed
      createdAt: dayjs().subtract(1, "day"),
    },
    {
      id: 2,
      dateTime: dayjs().add(5, "day"),
      description: "Check production progress",
      status: "pending",
      createdAt: dayjs().subtract(2, "day"),
    },
  ]);

  const handleReminder = () => {
    setIsReminderViewMode(true);
    setReminderModalOpen(true);
    handleMenuClose();
  };

  const handleAddNewReminder = () => {
    setIsReminderViewMode(false);
    setSelectedReminder(null);
    setReminderDateTime(null);
    setReminderDescription("");
  };

  const handleEditReminder = reminder => {
    setSelectedReminder(reminder);
    setReminderDateTime(reminder.dateTime);
    setReminderDescription(reminder.description);
    setIsReminderViewMode(false);
  };

  const handleSetReminder = async () => {
    try {
      // TODO: Implement reminder setting logic here
      if (selectedReminder) {
        // Update existing reminder
        const updatedReminders = reminders.map(r =>
          r.id === selectedReminder.id
            ? {
                ...r,
                dateTime: reminderDateTime,
                description: reminderDescription,
              }
            : r,
        );
        setReminders(updatedReminders);
      } else {
        // Add new reminder
        const newReminder = {
          id: reminders.length + 1,
          dateTime: reminderDateTime,
          description: reminderDescription,
          status: "pending",
          createdAt: dayjs(),
        };
        setReminders([...reminders, newReminder]);
      }

      // Reset state and show view mode
      setSelectedReminder(null);
      setReminderDateTime(null);
      setReminderDescription("");
      setIsReminderViewMode(true);
      // Don't close the modal, just show the view
    } catch (error) {
      console.error("Error setting reminder:", error);
    }
  };

  const handleToggleReminderStatus = reminderId => {
    const updatedReminders = reminders.map(r =>
      r.id === reminderId
        ? { ...r, status: r.status === "pending" ? "completed" : "pending" }
        : r,
    );
    setReminders(updatedReminders);
  };

  const handleSettleFormChange = (field, value) => {
    setSettleForm(prev => ({
      ...prev,
      [field]: field === "settleDateTime" ? dayjs(value) : value,
    }));
  };

  const handleSettleQuantityChange = (materialId, color, value) => {
    setSettleQuantities(prev => ({
      ...prev,
      [materialId]: {
        ...prev[materialId],
        [color]: value,
      },
    }));
  };

  const handleSettleMaterials = async () => {
    setIsSubmittingSettle(true);
    try {
      // TODO: Implement settle materials functionality
      setShowSettleMaterialsModal(false);
    } catch (error) {
      console.error("Error settling materials:", error);
    } finally {
      setIsSubmittingSettle(false);
    }
  };

  const handleMenuClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCalculateCosting = () => {
    // TODO: Implement costing calculation
    handleMenuClose();
  };

  const handleComplete = () => {
    // TODO: Implement completion functionality
    handleMenuClose();
  };

  const handleDelete = () => {
    // TODO: Implement delete functionality
    handleMenuClose();
  };

  const handleBillPhotoChange = event => {
    const file = event.target.files[0];
    if (file && billDetails.billPhotos.length < 3) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setBillDetails(prev => ({
          ...prev,
          billPhotos: [...prev.billPhotos, { file, preview: reader.result }],
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = index => {
    setBillDetails(prev => ({
      ...prev,
      billPhotos: prev.billPhotos.filter((_, i) => i !== index),
    }));
  };

  const handleBillInputChange = e => {
    const { name, value } = e.target;
    setBillDetails(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveBill = async () => {
    setIsSubmittingBill(true);
    try {
      // TODO: Implement save bill functionality
      setIsBillViewMode(true);
    } catch (error) {
      console.error("Error saving bill:", error);
    } finally {
      setIsSubmittingBill(false);
    }
  };

  const handleEditBill = () => {
    setIsBillViewMode(false);
  };

  const handleCancelBillEdit = () => {
    if (billDetails.billNo) {
      setIsBillViewMode(true);
    } else {
      setShowBillModal(false);
    }
  };

  const handleDeleteBill = () => {
    // TODO: Implement delete bill functionality
    setBillDetails({
      billPhotos: [],
      billNo: "",
      billerName: "",
      jobRate: "",
      billDate: "",
      billNote: "",
    });
    setShowDeleteBillDialog(false);
    setShowBillModal(false);
  };

  const handleSaveNotes = async () => {
    setIsSubmittingNotes(true);
    try {
      // TODO: Implement save notes functionality
      setIsNotesViewMode(true);
    } catch (error) {
      console.error("Error saving notes:", error);
    } finally {
      setIsSubmittingNotes(false);
    }
  };

  const handleEditNotes = () => {
    setIsNotesViewMode(false);
  };

  const handleCloseNotes = () => {
    if (!isSubmittingNotes) {
      setShowNotesModal(false);
      if (notes) {
        setIsNotesViewMode(true);
      }
    }
  };

  // Sample data - In real app, this would come from an API
  const [jobslip, setJobslip] = useState({
    id: "JS001",
    generatedDateTime: "2024-03-20 14:30",
    jobslipNo: "LOT123",
    karigar: {
      id: 1,
      name: "John Doe",
      specialization: "Cutting",
    },
    jobType: {
      id: 1,
      name: "Cutting",
    },
    agent: {
      id: 1,
      name: "Direct",
    },
    expectedDate: "2024-03-25",
    notes: "Priority order for regular customer",
    materials: [
      {
        id: 1,
        name: "Cotton Fabric",
        color: "White",
        quantity: 100,
        unit: "meters",
        rate: 150,
        photo: null,
        notes: "Premium quality fabric",
        taka: [
          { quantity: 20, value: 40 },
          { quantity: 30, value: 60 },
        ],
      },
      {
        id: 2,
        name: "Buttons",
        color: "Silver",
        quantity: 500,
        unit: "pieces",
        rate: 2,
        photo: null,
      },
    ],
    designs: [
      {
        id: 1,
        name: "Floral Pattern Dress",
        designId: "DSG001",
        image: null,
        pieces: [
          {
            pieceType: "Bottom",
            jobRate: 200,
            sizeRatio: {
              S: "1",
              M: "2",
              L: "2",
              XL: "1",
            },
            colors: [
              {
                name: "Blue",
                quantity: 50,
                unit: "pieces",
                sizes: {
                  S: 10,
                  M: 20,
                  L: 15,
                  XL: 5,
                },
              },
            ],
          },
        ],
      },
    ],
  });

  const handleBack = () => {
    if (isEditMode) {
      setShowCancelDialog(true);
    } else {
      navigate("/production/jobslips");
    }
  };

  const handleEdit = () => {
    navigate(`/production/jobslips/edit?id=${jobslip.id}`);
  };

  const handleCancelEdit = () => {
    setShowCancelDialog(true);
  };

  const [settleDesignForm, setSettleDesignForm] = useState({
    settleReason: "",
    settleDateTime: dayjs(),
    settledBy: "Thomas", // Non-editable
    settleChallanNo: "",
    settleNote: "",
  });

  const [showSettleDesignModal, setShowSettleDesignModal] = useState(false);
  const [isSubmittingDesignSettle, setIsSubmittingDesignSettle] =
    useState(false);
  const [settleDesignQuantities, setSettleDesignQuantities] = useState({});

  const handleSettleDesignFormChange = (field, value) => {
    setSettleDesignForm(prev => ({
      ...prev,
      [field]: field === "settleDateTime" ? dayjs(value) : value,
    }));
  };

  const handleSettleDesignQuantityChange = (designId, colorId, size, value) => {
    setSettleDesignQuantities(prev => ({
      ...prev,
      [designId]: {
        ...prev[designId],
        [colorId]: {
          ...prev[designId]?.[colorId],
          [size]: value,
        },
      },
    }));
  };

  const handleSettleDesigns = async () => {
    setIsSubmittingDesignSettle(true);
    try {
      // TODO: Implement settle designs functionality
      setShowSettleDesignModal(false);
    } catch (error) {
      console.error("Error settling designs:", error);
    } finally {
      setIsSubmittingDesignSettle(false);
    }
  };

  return (
    <div className="bg-white h-full">
      {/* Header Section */}
      <div className="flex items-center justify-between px-6 py-4 border-b">
        <div className="flex items-center gap-4">
          <button
            onClick={handleBack}
            className="p-1.5 rounded-md bg-gray-50 hover:bg-gray-100 transition-colors">
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
          <h1 className="text-2xl font-semibold font-outfit text-gray-800">
            Job Slip Details
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              navigate(`/production/jobslips/receive/${jobslip.id}`);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-[#5652B0] text-white rounded-lg text-sm font-medium hover:bg-[#4A47A3] transition-all border border-[#5652B0]">
            <PackageCheck size={16} />
            Receive
          </button>
          <button
            onClick={() => {
              navigate(`/production/jobslips/transfer/${jobslip.id}`);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-[#5652B0] text-white rounded-lg text-sm font-medium hover:bg-[#4A47A3] transition-all border border-[#5652B0]">
            <Copy size={16} />
            Transfer
          </button>
          <button
            onClick={handleEdit}
            className="flex items-center gap-2 px-4 py-2 bg-white text-[#5652B0] rounded-lg text-sm font-medium hover:bg-[#5652B0] hover:text-white transition-all border border-[#5652B0]">
            <Edit size={16} />
            Edit Job Slip
          </button>
          <div className="h-6 w-px bg-gray-200 mx-1"></div>
          <button
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
            onClick={() => {
              /* TODO: Implement share functionality */
            }}>
            <Share2 size={20} />
          </button>
          <button
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
            onClick={() => {
              /* TODO: Implement print functionality */
            }}>
            <Printer size={20} />
          </button>
          <button
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
            onClick={handleMenuClick}>
            <MoreVertical size={20} />
          </button>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            PaperProps={{
              sx: {
                mt: 1,
                boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                borderRadius: "8px",
                minWidth: "200px",
              },
            }}>
            <MenuItem onClick={handleReminder} sx={{ py: 1.5 }}>
              <ListItemIcon>
                <Bell size={18} className="text-gray-600" />
              </ListItemIcon>
              <ListItemText>Reminders</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleCalculateCosting} sx={{ py: 1.5 }}>
              <ListItemIcon>
                <Calculator size={18} className="text-gray-600" />
              </ListItemIcon>
              <ListItemText>Calculate Costing</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleComplete} sx={{ py: 1.5 }}>
              <ListItemIcon>
                <CheckCircle2 size={18} className="text-[#22C55E]" />
              </ListItemIcon>
              <ListItemText>Complete</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleDelete} sx={{ py: 1.5 }}>
              <ListItemIcon>
                <Trash2 size={18} className="text-[#EF4444]" />
              </ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
          </Menu>
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 gap-6">
          {/* Basic Information */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h1 className="text-base font-semibold text-gray-800 text-left">
                Basic Information
              </h1>
              <button
                onClick={() => setShowBillModal(true)}
                className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-[#5652B0] bg-[#5652B0]/5 hover:bg-[#5652B0]/10 rounded-lg transition-colors">
                <FileText size={16} />
                {billDetails.billNo ? "Edit Bill No." : "Add Bill No."}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="flex items-center gap-2 w-48">
                    <Clock size={18} className="text-[#5652B0]" />
                    <label className="text-sm font-medium text-gray-500 text-left">
                      Date and Time of Generating Slip:
                    </label>
                  </div>
                  <div className="text-gray-900 text-left">
                    {jobslip.generatedDateTime}
                  </div>
                </div>

                <div className="flex items-center">
                  <div className="flex items-center gap-2 w-48">
                    <FileText size={18} className="text-[#5652B0]" />
                    <label className="text-sm font-medium text-gray-500 text-left">
                      Lot No/Manual Jobslip No:
                    </label>
                  </div>
                  <div className="text-gray-900 text-left">
                    {jobslip.jobslipNo}
                  </div>
                </div>

                <div className="flex items-center">
                  <div className="flex items-center gap-2 w-48">
                    <User2 size={18} className="text-[#5652B0]" />
                    <label className="text-sm font-medium text-gray-500 text-left">
                      Karigar:
                    </label>
                  </div>
                  <div className="text-gray-900 text-left">
                    {jobslip.karigar.name} - {jobslip.karigar.specialization}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="flex items-center gap-2 w-48">
                    <Briefcase size={18} className="text-[#5652B0]" />
                    <label className="text-sm font-medium text-gray-500 text-left">
                      Job Type:
                    </label>
                  </div>
                  <div className="text-gray-900 text-left">
                    {jobslip.jobType.name}
                  </div>
                </div>

                <div className="flex items-center">
                  <div className="flex items-center gap-2 w-48">
                    <Building2 size={18} className="text-[#5652B0]" />
                    <label className="text-sm font-medium text-gray-500 text-left">
                      Agent:
                    </label>
                  </div>
                  <div className="text-gray-900 text-left">
                    {jobslip.agent.name}
                  </div>
                </div>

                <div className="flex items-center">
                  <div className="flex items-center gap-2 w-48">
                    <Calendar size={18} className="text-[#5652B0]" />
                    <label className="text-sm font-medium text-gray-500 text-left">
                      Expected Date:
                    </label>
                  </div>
                  <div className="text-gray-900 text-left">
                    {jobslip.expectedDate}
                  </div>
                </div>
              </div>
            </div>

            {jobslip.notes && (
              <div className="mt-6 flex items-start flex-col">
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Notes
                </label>
                <div className="flex items-start gap-2 text-gray-900 bg-gray-50 p-3 rounded-lg w-full">
                  <FileText size={18} className="text-[#5652B0] mt-0.5" />
                  {jobslip.notes}
                </div>
              </div>
            )}
          </div>

          {/* Materials Section */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h1 className="text-base font-semibold text-gray-800 text-left">
                Materials
              </h1>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowNotesModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-white text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50 transition-all border border-gray-300">
                  <StickyNote size={16} />
                  Notes
                </button>
                <button
                  onClick={() => {
                    setShowSettleMaterialsModal(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-white text-[#5652B0] rounded-lg text-sm font-medium hover:bg-[#5652B0] hover:text-white transition-all border border-[#5652B0]">
                  <CornerUpLeft size={16} />
                  Settle Materials
                </button>
              </div>
            </div>
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: "none",
                border: "1px solid #E5E7EB",
                borderRadius: "0.5rem",
              }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#F9FAFB" }}>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "14px",
                        color: "#374151",
                        borderBottom: "2px solid #E5E7EB",
                      }}>
                      Material
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "14px",
                        color: "#374151",
                        borderBottom: "2px solid #E5E7EB",
                      }}>
                      Color
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "14px",
                        color: "#374151",
                        borderBottom: "2px solid #E5E7EB",
                      }}>
                      Quantity
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "14px",
                        color: "#374151",
                        borderBottom: "2px solid #E5E7EB",
                      }}>
                      Received Qty/Settled Qty
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "14px",
                        color: "#374151",
                        borderBottom: "2px solid #E5E7EB",
                      }}>
                      Remaining Qty
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "14px",
                        color: "#374151",
                        borderBottom: "2px solid #E5E7EB",
                      }}>
                      Taka
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobslip.materials.map(material => (
                    <TableRow key={material.id} hover>
                      <TableCell sx={{ fontSize: "14px", color: "#4B5563" }}>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg border border-gray-200 overflow-hidden bg-gray-50">
                            {material.photo ? (
                              <img
                                src={material.photo}
                                alt={material.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-400">
                                <ImageIcon size={20} />
                              </div>
                            )}
                          </div>
                          <div>
                            <div className="font-medium">{material.name}</div>
                            {material.notes && (
                              <div className="text-sm text-gray-500">
                                {material.notes}
                              </div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell sx={{ fontSize: "14px", color: "#4B5563" }}>
                        {material.color}
                      </TableCell>
                      <TableCell sx={{ fontSize: "14px", color: "#4B5563" }}>
                        {material.quantity} {material.unit}
                      </TableCell>
                      <TableCell sx={{ fontSize: "14px", color: "#4B5563" }}>
                        {material.receivedQty || 0} {material.unit}/
                        {material.settledQty || 0} {material.unit}
                      </TableCell>
                      <TableCell sx={{ fontSize: "14px", color: "#4B5563" }}>
                        {material.quantity -
                          ((material.receivedQty || 0) +
                            (material.settledQty || 0))}{" "}
                        {material.unit}
                      </TableCell>
                      <TableCell sx={{ fontSize: "14px", color: "#4B5563" }}>
                        {material.taka && material.taka.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {material.taka.map((taka, index) => (
                              <span
                                key={index}
                                className="px-2 py-1 bg-gray-100 text-xs font-medium rounded">
                                {taka.quantity}x{taka.value}
                              </span>
                            ))}
                          </div>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ backgroundColor: "#F9FAFB" }}>
                    <TableCell
                      colSpan={3}
                      sx={{
                        fontWeight: "600",
                        fontSize: "14px",
                        color: "#374151",
                        borderTop: "2px solid #E5E7EB",
                      }}>
                      Total
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "14px",
                        color: "#374151",
                        borderTop: "2px solid #E5E7EB",
                      }}>
                      {jobslip.materials.reduce(
                        (sum, material) => sum + (material.receivedQty || 0),
                        0,
                      )}{" "}
                      {jobslip.materials[0]?.unit}/
                      {jobslip.materials.reduce(
                        (sum, material) => sum + (material.settledQty || 0),
                        0,
                      )}{" "}
                      {jobslip.materials[0]?.unit}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "14px",
                        color: "#374151",
                        borderTop: "2px solid #E5E7EB",
                      }}>
                      {jobslip.materials.reduce(
                        (sum, material) =>
                          sum +
                          (material.quantity -
                            ((material.receivedQty || 0) +
                              (material.settledQty || 0))),
                        0,
                      )}{" "}
                      {jobslip.materials[0]?.unit}
                    </TableCell>
                    <TableCell
                      sx={{ borderTop: "2px solid #E5E7EB" }}></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </div>

          {/* Designs Section */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h1 className="text-base font-semibold text-gray-800 text-left">
                Design Information
              </h1>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    /* TODO: Implement design notes functionality */
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-white text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50 transition-all border border-gray-300">
                  <StickyNote size={16} />
                  Notes
                </button>
                <button
                  onClick={() => {
                    setShowSettleDesignModal(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-white text-[#5652B0] rounded-lg text-sm font-medium hover:bg-[#5652B0] hover:text-white transition-all border border-[#5652B0]">
                  <CornerUpLeft size={16} />
                  Settle Design
                </button>
              </div>
            </div>
            {jobslip.designs.map(design => (
              <div
                key={design.id}
                className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="p-4 bg-gray-50 border-b border-gray-200">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 flex-shrink-0 rounded-lg border border-gray-200 overflow-hidden bg-gray-50">
                      {design.image ? (
                        <img
                          src={design.image}
                          alt={design.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-300">
                          <ImageIcon size={18} />
                        </div>
                      )}
                    </div>
                    <div className="flex flex-row justify-between w-full">
                      <div className="flex flex-col items-start">
                        <h3 className="text-lg font-medium text-gray-900">
                          {design.name}
                        </h3>
                        <p className="text-sm text-gray-500 mt-0.5">
                          Design ID: {design.designId}
                        </p>
                      </div>
                      {design.pieces.map((piece, pieceIndex) => (
                        <div key={pieceIndex} className="flex flex-col gap-2">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1.5">
                              <span className="text-sm text-gray-500">
                                Piece:
                              </span>
                              <span className="text-sm font-medium text-[#5652B0] bg-[#5652B0]/5 px-2 py-0.5 rounded">
                                {piece.pieceType}
                              </span>
                            </div>
                            <span className="text-sm text-gray-500">
                              Job Rate: ₹{piece.jobRate}
                            </span>
                          </div>
                          {piece.sizeRatio &&
                            Object.keys(piece.sizeRatio).length > 0 && (
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-500">
                                  Size Ratio:
                                </span>
                                <div className="flex items-center gap-2 text-sm">
                                  {Object.entries(piece.sizeRatio).map(
                                    ([size, ratio], index, array) => (
                                      <React.Fragment key={size}>
                                        <span>
                                          {size}:{" "}
                                          <span className="font-medium text-gray-900">
                                            {ratio}
                                          </span>
                                        </span>
                                        {index < array.length - 1 && (
                                          <span className="text-gray-300 mx-1">
                                            •
                                          </span>
                                        )}
                                      </React.Fragment>
                                    ),
                                  )}
                                </div>
                              </div>
                            )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {design.pieces.map((piece, pieceIndex) => (
                  <div key={pieceIndex} className="p-4">
                    {/* Colors Table */}
                    <TableContainer
                      component={Paper}
                      sx={{
                        boxShadow: "none",
                        border: "1px solid #E5E7EB",
                        borderRadius: "0.5rem",
                      }}>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: "#F9FAFB" }}>
                            <TableCell
                              sx={{
                                fontWeight: "600",
                                fontSize: "14px",
                                color: "#374151",
                                borderBottom: "2px solid #E5E7EB",
                              }}>
                              Color
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: "600",
                                fontSize: "14px",
                                color: "#374151",
                                borderBottom: "2px solid #E5E7EB",
                              }}>
                              Quantity
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: "600",
                                fontSize: "14px",
                                color: "#374151",
                                borderBottom: "2px solid #E5E7EB",
                              }}>
                              Received Qty/Settled Qty
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: "600",
                                fontSize: "14px",
                                color: "#374151",
                                borderBottom: "2px solid #E5E7EB",
                              }}>
                              Size Distribution
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {piece.colors.map((color, colorIndex) => (
                            <TableRow key={colorIndex} hover>
                              <TableCell
                                sx={{ fontSize: "14px", color: "#4B5563" }}>
                                {color.name}
                              </TableCell>
                              <TableCell
                                sx={{ fontSize: "14px", color: "#4B5563" }}>
                                {color.quantity} {color.unit}
                              </TableCell>
                              <TableCell
                                sx={{ fontSize: "14px", color: "#4B5563" }}>
                                {color.receivedQty || 0} {color.unit}/
                                {color.settledQty || 0} {color.unit}
                              </TableCell>
                              <TableCell
                                sx={{ fontSize: "14px", color: "#4B5563" }}>
                                {color.sizes &&
                                Object.keys(color.sizes).length > 0 ? (
                                  <div className="flex items-center gap-3">
                                    {Object.entries(color.sizes).map(
                                      ([size, qty], index, array) => (
                                        <React.Fragment key={size}>
                                          <span className="text-sm">
                                            {size}:{" "}
                                            <span className="font-medium">
                                              {qty}
                                            </span>
                                          </span>
                                          {index < array.length - 1 && (
                                            <span className="text-gray-300">
                                              •
                                            </span>
                                          )}
                                        </React.Fragment>
                                      ),
                                    )}
                                  </div>
                                ) : (
                                  "-"
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* History Sections */}
          <div className="space-y-4">
            {/* Receive History */}
            <button
              onClick={() => setShowReceiveHistoryModal(true)}
              className="w-full bg-white border border-gray-200 rounded-lg px-6 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors">
              <h1 className="text-base font-semibold text-gray-800">
                Receive History
              </h1>
              <ChevronRight size={20} className="text-gray-400" />
            </button>

            {/* Transfer History */}
            <button
              onClick={() => setShowTransferHistoryModal(true)}
              className="w-full bg-white border border-gray-200 rounded-lg px-6 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors">
              <h1 className="text-base font-semibold text-gray-800">
                Transfer History
              </h1>
              <ChevronRight size={20} className="text-gray-400" />
            </button>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center justify-end gap-4 mt-6 pb-8">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 border border-gray-300 hover:bg-gray-50 rounded-lg text-sm font-medium transition-colors">
            <ArrowLeft size={16} />
            Back to Job Slips
          </button>
          <button
            onClick={() => {
              navigate(`/production/jobslips/receive/${jobslip.id}`);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-[#5652B0] text-white rounded-lg text-sm font-medium hover:bg-[#4A47A3] transition-all border border-[#5652B0]">
            <PackageCheck size={16} />
            Receive
          </button>
          <button
            onClick={() => {
              navigate(`/production/jobslips/transfer/${jobslip.id}`);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-[#5652B0] text-white rounded-lg text-sm font-medium hover:bg-[#4A47A3] transition-all border border-[#5652B0]">
            <Copy size={16} />
            Transfer
          </button>
          <button
            onClick={handleEdit}
            className="flex items-center gap-2 px-4 py-2 bg-white text-[#5652B0] rounded-lg text-sm font-medium hover:bg-[#5652B0] hover:text-white transition-all border border-[#5652B0]">
            <Edit size={16} />
            Edit Job Slip
          </button>
        </div>
      </div>

      {/* Receive History Modal */}
      <Dialog
        open={showReceiveHistoryModal}
        onClose={() => setShowReceiveHistoryModal(false)}
        maxWidth="md"
        fullWidth>
        <DialogTitle className="flex items-center justify-between border-b p-4">
          <span className="text-lg font-semibold text-gray-900">
            Receive History
          </span>
          <IconButton
            onClick={() => setShowReceiveHistoryModal(false)}
            size="small"
            className="text-gray-500 hover:text-gray-700">
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent className="p-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sr. No
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Item Name
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Qty
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pending
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {/* Design Received Section */}
                <tr className="bg-indigo-50">
                  <td colSpan="5" className="px-6 py-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-indigo-900">
                        Design Received
                      </span>
                      <div className="flex items-center gap-3">
                        <button className="text-gray-600 hover:text-indigo-900 transition-colors">
                          <Printer size={18} />
                        </button>
                        <button className="text-gray-600 hover:text-indigo-900 transition-colors">
                          <CornerUpLeft size={18} />
                        </button>
                        <button className="text-gray-600 hover:text-indigo-900 transition-colors">
                          <Edit size={18} />
                        </button>
                        <button className="text-gray-600 hover:text-indigo-900 transition-colors">
                          <Trash2 size={18} />
                        </button>
                        <button className="text-gray-600 hover:text-indigo-900 transition-colors">
                          <StickyNote size={18} />
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    1
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    Design A
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    100 pcs
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    50 pcs
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    50 pcs
                  </td>
                </tr>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <td colSpan="5" className="px-6 py-2 text-xs text-gray-500">
                    Received by:{" "}
                    <span className="font-medium text-gray-900">John Doe</span>{" "}
                    • <span className="text-gray-500">2024-03-20 14:30</span>
                  </td>
                </tr>

                {/* Design Returned Section */}
                <tr className="bg-red-50">
                  <td colSpan="5" className="px-6 py-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-red-900">
                        Design Returned
                      </span>
                      <div className="flex items-center gap-3">
                        <button className="text-gray-600 hover:text-red-900 transition-colors">
                          <Printer size={18} />
                        </button>
                        <button className="text-gray-600 hover:text-red-900 transition-colors">
                          <Edit size={18} />
                        </button>
                        <button className="text-gray-600 hover:text-red-900 transition-colors">
                          <Trash2 size={18} />
                        </button>
                        <button className="text-gray-600 hover:text-red-900 transition-colors">
                          <StickyNote size={18} />
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    1
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    Design B
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    75 pcs
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    25 pcs
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    50 pcs
                  </td>
                </tr>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <td colSpan="5" className="px-6 py-2 text-xs text-gray-500">
                    Received by:{" "}
                    <span className="font-medium text-gray-900">
                      Jane Smith
                    </span>{" "}
                    • <span className="text-gray-500">2024-03-19 11:45</span>
                  </td>
                </tr>

                {/* Material Returned Section */}
                <tr className="bg-red-50">
                  <td colSpan="5" className="px-6 py-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-red-900">
                        Material Returned
                      </span>
                      <div className="flex items-center gap-3">
                        <button className="text-gray-600 hover:text-red-900 transition-colors">
                          <Printer size={18} />
                        </button>
                        <button className="text-gray-600 hover:text-red-900 transition-colors">
                          <Edit size={18} />
                        </button>
                        <button className="text-gray-600 hover:text-red-900 transition-colors">
                          <Trash2 size={18} />
                        </button>
                        <button className="text-gray-600 hover:text-red-900 transition-colors">
                          <StickyNote size={18} />
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    1
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    Material X
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    200 kg
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    150 kg
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    50 kg
                  </td>
                </tr>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <td colSpan="5" className="px-6 py-2 text-xs text-gray-500">
                    Received by:{" "}
                    <span className="font-medium text-gray-900">
                      Mike Johnson
                    </span>{" "}
                    • <span className="text-gray-500">2024-03-18 16:20</span>
                  </td>
                </tr>

                {/* Material Received Section */}
                <tr className="bg-indigo-50">
                  <td colSpan="5" className="px-6 py-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-indigo-900">
                        Material Received
                      </span>
                      <div className="flex items-center gap-3">
                        <button className="text-gray-600 hover:text-indigo-900 transition-colors">
                          <Printer size={18} />
                        </button>
                        <button className="text-gray-600 hover:text-indigo-900 transition-colors">
                          <CornerUpLeft size={18} />
                        </button>
                        <button className="text-gray-600 hover:text-indigo-900 transition-colors">
                          <Edit size={18} />
                        </button>
                        <button className="text-gray-600 hover:text-indigo-900 transition-colors">
                          <Trash2 size={18} />
                        </button>
                        <button className="text-gray-600 hover:text-indigo-900 transition-colors">
                          <StickyNote size={18} />
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    1
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    Material Y
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    300 kg
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    200 kg
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    100 kg
                  </td>
                </tr>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <td colSpan="5" className="px-6 py-2 text-xs text-gray-500">
                    Received by:{" "}
                    <span className="font-medium text-gray-900">
                      Sarah Wilson
                    </span>{" "}
                    • <span className="text-gray-500">2024-03-17 09:15</span>
                  </td>
                </tr>

                {/* Processed Material Received Section */}
                <tr className="bg-indigo-50">
                  <td colSpan="5" className="px-6 py-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-indigo-900">
                        Processed Material Received
                      </span>
                      <div className="flex items-center gap-3">
                        <button className="text-gray-600 hover:text-indigo-900 transition-colors">
                          <Printer size={18} />
                        </button>
                        <button className="text-gray-600 hover:text-indigo-900 transition-colors">
                          <Edit size={18} />
                        </button>
                        <button className="text-gray-600 hover:text-indigo-900 transition-colors">
                          <Trash2 size={18} />
                        </button>
                        <button className="text-gray-600 hover:text-indigo-900 transition-colors">
                          <StickyNote size={18} />
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    1
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    Processed Material Z
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    150 kg
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    100 kg
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    50 kg
                  </td>
                </tr>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <td colSpan="5" className="px-6 py-2 text-xs text-gray-500">
                    Received by:{" "}
                    <span className="font-medium text-gray-900">
                      Robert Brown
                    </span>{" "}
                    • <span className="text-gray-500">2024-03-16 13:40</span>
                  </td>
                </tr>

                {/* Other Design Received Section */}
                <tr className="bg-indigo-50">
                  <td colSpan="5" className="px-6 py-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-indigo-900">
                        Other Design Received
                      </span>
                      <div className="flex items-center gap-3">
                        <button className="text-gray-600 hover:text-indigo-900 transition-colors">
                          <Printer size={18} />
                        </button>
                        <button className="text-gray-600 hover:text-indigo-900 transition-colors">
                          <Edit size={18} />
                        </button>
                        <button className="text-gray-600 hover:text-indigo-900 transition-colors">
                          <Trash2 size={18} />
                        </button>
                        <button className="text-gray-600 hover:text-indigo-900 transition-colors">
                          <StickyNote size={18} />
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    1
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    Other Design C
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    50 pcs
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    30 pcs
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    20 pcs
                  </td>
                </tr>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <td colSpan="5" className="px-6 py-2 text-xs text-gray-500">
                    Received by:{" "}
                    <span className="font-medium text-gray-900">
                      Emily Davis
                    </span>{" "}
                    • <span className="text-gray-500">2024-03-15 10:55</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Transfer History Modal */}
      <Dialog
        open={showTransferHistoryModal}
        onClose={() => setShowTransferHistoryModal(false)}
        maxWidth="md"
        fullWidth>
        <DialogTitle className="flex items-center justify-between border-b p-4">
          <span className="text-lg font-semibold text-gray-900">
            Transfer History
          </span>
          <IconButton
            onClick={() => setShowTransferHistoryModal(false)}
            size="small"
            className="text-gray-500 hover:text-gray-700">
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent className="p-6">
          <div className="space-y-6">
            {/* Transfer Entry Card 1 */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
              {/* Header Information */}
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <div className="flex justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600">Jobslip no.</span>
                    <span className="font-medium">JOB-1</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600">Transfer challan no.</span>
                    <span className="font-medium">1234</span>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Transfer Details Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left text-xs font-medium text-gray-500 uppercase pb-3">
                          Date
                        </th>
                        <th className="text-left text-xs font-medium text-gray-500 uppercase pb-3">
                          Transfer to
                        </th>
                        <th className="text-left text-xs font-medium text-gray-500 uppercase pb-3">
                          Job Type
                        </th>
                        <th className="text-left text-xs font-medium text-gray-500 uppercase pb-3">
                          Rate
                        </th>
                        <th className="text-left text-xs font-medium text-gray-500 uppercase pb-3">
                          Note
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="py-3 text-sm text-gray-900">
                          2024-03-20
                        </td>
                        <td className="py-3 text-sm text-gray-900">
                          Workshop A
                        </td>
                        <td className="py-3 text-sm text-gray-900">Cutting</td>
                        <td className="py-3 text-sm text-gray-900">₹500</td>
                        <td className="py-3 text-sm text-gray-900">
                          Regular cut
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Item Information Section */}
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">
                    Item Information
                  </h2>
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left text-xs font-medium text-gray-500 uppercase pb-3">
                            Serial No
                          </th>
                          <th className="text-left text-xs font-medium text-gray-500 uppercase pb-3">
                            Item Name
                          </th>
                          <th className="text-left text-xs font-medium text-gray-500 uppercase pb-3">
                            Quantity
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="py-3 text-sm text-gray-900">1</td>
                          <td className="py-3 text-sm text-gray-900">
                            Cotton Fabric
                          </td>
                          <td className="py-3 text-sm text-gray-900">
                            50 meters
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* Transfer Entry Card 2 */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
              {/* Header Information */}
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <div className="flex justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600">Jobslip no.</span>
                    <span className="font-medium">JOB-1</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600">Transfer challan no.</span>
                    <span className="font-medium">1235</span>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Transfer Details Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left text-xs font-medium text-gray-500 uppercase pb-3">
                          Date
                        </th>
                        <th className="text-left text-xs font-medium text-gray-500 uppercase pb-3">
                          Transfer to
                        </th>
                        <th className="text-left text-xs font-medium text-gray-500 uppercase pb-3">
                          Job Type
                        </th>
                        <th className="text-left text-xs font-medium text-gray-500 uppercase pb-3">
                          Rate
                        </th>
                        <th className="text-left text-xs font-medium text-gray-500 uppercase pb-3">
                          Note
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="py-3 text-sm text-gray-900">
                          2024-03-21
                        </td>
                        <td className="py-3 text-sm text-gray-900">
                          Workshop B
                        </td>
                        <td className="py-3 text-sm text-gray-900">
                          Stitching
                        </td>
                        <td className="py-3 text-sm text-gray-900">₹800</td>
                        <td className="py-3 text-sm text-gray-900">
                          Special stitch
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Item Information Section */}
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">
                    Item Information
                  </h2>
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left text-xs font-medium text-gray-500 uppercase pb-3">
                            Serial No
                          </th>
                          <th className="text-left text-xs font-medium text-gray-500 uppercase pb-3">
                            Item Name
                          </th>
                          <th className="text-left text-xs font-medium text-gray-500 uppercase pb-3">
                            Quantity
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="py-3 text-sm text-gray-900">1</td>
                          <td className="py-3 text-sm text-gray-900">
                            Buttons
                          </td>
                          <td className="py-3 text-sm text-gray-900">
                            200 pcs
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Bill Modal */}
      <Dialog
        open={showBillModal}
        onClose={() => {
          if (!isSubmittingBill) {
            setShowBillModal(false);
            if (billDetails.billNo) {
              setIsBillViewMode(true);
            }
          }
        }}
        maxWidth="sm"
        fullWidth>
        <DialogTitle className="flex items-center justify-between border-b p-4">
          <span className="text-lg font-semibold text-gray-900">
            {isBillViewMode
              ? "Bill Details"
              : billDetails.billNo
              ? "Edit Bill"
              : "Add Bill"}
          </span>
          <IconButton
            onClick={() => {
              setShowBillModal(false);
              if (billDetails.billNo) {
                setIsBillViewMode(true);
              }
            }}
            size="small"
            className="text-gray-500 hover:text-gray-700">
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent className="p-6">
          <div className="space-y-6">
            {/* Bill Photos Upload/View */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                Bill Photos {!isBillViewMode && "(Max 3)"}
              </h3>
              <div className="mt-1 flex gap-3">
                {billDetails.billPhotos.map((photo, index) => (
                  <div
                    key={index}
                    className={`w-40 aspect-square rounded-lg overflow-hidden ${
                      isBillViewMode
                        ? "border border-gray-200"
                        : "border-2 border-gray-300 border-dashed hover:border-gray-400"
                    } transition-colors relative`}>
                    <div className="w-full h-full relative group">
                      <img
                        src={photo.preview}
                        alt={`Bill preview ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                      {!isBillViewMode && (
                        <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <button
                            type="button"
                            onClick={() => handleRemovePhoto(index)}
                            className="p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors">
                            <X size={16} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {!isBillViewMode && billDetails.billPhotos.length < 3 && (
                  <div className="w-40 aspect-square border-2 border-gray-300 border-dashed rounded-lg hover:border-gray-400 transition-colors relative overflow-hidden">
                    <div className="absolute inset-0 flex flex-col items-center justify-center p-2">
                      <Upload className="h-8 w-8 text-gray-400 mb-2" />
                      <div className="flex flex-col items-center text-sm text-gray-600">
                        <label
                          htmlFor="bill-photo-upload"
                          className="relative cursor-pointer rounded-md font-medium text-[#5652B0] hover:text-[#4A47A3] focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-[#5652B0]">
                          <span>Upload a file</span>
                          <input
                            id="bill-photo-upload"
                            name="billPhoto"
                            type="file"
                            className="sr-only"
                            onChange={handleBillPhotoChange}
                            accept="image/*"
                          />
                        </label>
                        <p className="text-xs text-gray-500 mt-1">
                          or drag and drop
                        </p>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        PNG, JPG, GIF
                      </p>
                    </div>
                  </div>
                )}
              </div>
              {!isBillViewMode && billDetails.billPhotos.length >= 3 && (
                <p className="text-xs text-red-500 mt-1">
                  Maximum 3 photos allowed
                </p>
              )}
            </div>

            {/* Form Fields */}
            {isBillViewMode ? (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-gray-500">Bill No</p>
                    <p className="text-base text-gray-900">
                      {billDetails.billNo}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-gray-500">
                      Biller Name
                    </p>
                    <p className="text-base text-gray-900">
                      {billDetails.billerName || "-"}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-gray-500">
                      Job Rate
                    </p>
                    <p className="text-base text-gray-900">
                      ₹{billDetails.jobRate || "-"}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-gray-500">
                      Bill Date
                    </p>
                    <p className="text-base text-gray-900">
                      {billDetails.billDate || "-"}
                    </p>
                  </div>
                </div>
                {billDetails.billNote && (
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-gray-500">
                      Bill Note
                    </p>
                    <p className="text-base text-gray-900 bg-gray-50 p-3 rounded-lg">
                      {billDetails.billNote}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4">
                  {/* Bill No */}
                  <TextField
                    required
                    fullWidth
                    name="billNo"
                    label="Bill No"
                    value={billDetails.billNo}
                    onChange={handleBillInputChange}
                    size="small"
                  />

                  {/* Biller Name */}
                  <TextField
                    fullWidth
                    name="billerName"
                    label="Biller Name"
                    value={billDetails.billerName}
                    onChange={handleBillInputChange}
                    size="small"
                  />

                  {/* Job Rate */}
                  <TextField
                    fullWidth
                    name="jobRate"
                    label="Job Rate"
                    value={billDetails.jobRate}
                    onChange={handleBillInputChange}
                    size="small"
                    type="number"
                  />

                  {/* Bill Date */}
                  <TextField
                    fullWidth
                    name="billDate"
                    label="Bill Date"
                    type="date"
                    value={billDetails.billDate}
                    onChange={handleBillInputChange}
                    size="small"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </div>

                {/* Bill Note */}
                <div className="mt-4">
                  <TextField
                    fullWidth
                    name="billNote"
                    label="Bill Note"
                    value={billDetails.billNote}
                    onChange={handleBillInputChange}
                    size="small"
                    multiline
                    rows={3}
                  />
                </div>
              </>
            )}
          </div>
        </DialogContent>
        <DialogActions className="border-t p-4">
          {isBillViewMode ? (
            <>
              <button
                onClick={() => setShowBillModal(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">
                Close
              </button>
              <div className="flex-1"></div>
              <button
                onClick={() => setShowDeleteBillDialog(true)}
                className="px-4 py-2 text-red-600 bg-white border border-red-200 rounded-lg text-sm font-medium hover:bg-red-50 transition-colors flex items-center gap-2">
                <Trash2 size={16} />
                Delete Bill
              </button>
              <button
                onClick={handleEditBill}
                className="px-4 py-2 text-white bg-[#5652B0] rounded-lg text-sm font-medium hover:bg-[#4A47A3] transition-colors ml-3 flex items-center gap-2">
                <Edit size={16} />
                Edit
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleCancelBillEdit}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button
                onClick={handleSaveBill}
                disabled={!billDetails.billNo || isSubmittingBill}
                className="px-4 py-2 text-white bg-[#5652B0] rounded-lg text-sm font-medium hover:bg-[#4A47A3] transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed ml-3 flex items-center gap-2">
                {isSubmittingBill ? (
                  <>
                    <CircularProgress size={16} color="inherit" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Save Bill
                  </>
                )}
              </button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Bill Confirmation Dialog */}
      <Dialog
        open={showDeleteBillDialog}
        onClose={() => setShowDeleteBillDialog(false)}
        maxWidth="xs"
        fullWidth>
        <DialogTitle className="flex items-center gap-2 border-b p-4">
          <Trash2 size={20} className="text-red-600" />
          <span className="text-lg font-semibold text-gray-900">
            Delete Bill
          </span>
        </DialogTitle>
        <DialogContent className="p-6">
          <DialogContentText className="text-gray-600">
            Are you sure you want to delete this bill? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions className="border-t p-4">
          <button
            onClick={() => setShowDeleteBillDialog(false)}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">
            Cancel
          </button>
          <button
            onClick={handleDeleteBill}
            className="px-4 py-2 text-white bg-red-600 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors ml-3 flex items-center gap-2">
            <Trash2 size={16} />
            Delete Bill
          </button>
        </DialogActions>
      </Dialog>

      {/* Notes Modal */}
      <Dialog
        open={showNotesModal}
        onClose={handleCloseNotes}
        maxWidth="sm"
        fullWidth>
        <DialogTitle className="flex items-center justify-between border-b p-4">
          <span className="text-lg font-semibold text-gray-900">
            Material Notes
          </span>
          <IconButton
            onClick={handleCloseNotes}
            size="small"
            className="text-gray-500 hover:text-gray-700">
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent className="p-6">
          {isNotesViewMode ? (
            <div className="bg-gray-50 p-4 rounded-lg text-gray-900">
              {notes}
            </div>
          ) : (
            <TextField
              fullWidth
              multiline
              rows={4}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Enter notes about materials..."
              variant="outlined"
            />
          )}
        </DialogContent>
        <DialogActions className="border-t p-4">
          <button
            onClick={handleCloseNotes}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">
            {isNotesViewMode ? "Close" : "Cancel"}
          </button>
          {isNotesViewMode ? (
            <button
              onClick={handleEditNotes}
              className="px-4 py-2 text-white bg-[#5652B0] rounded-lg text-sm font-medium hover:bg-[#4A47A3] transition-colors ml-3 flex items-center gap-2">
              <Edit size={16} />
              Edit Notes
            </button>
          ) : (
            <button
              onClick={handleSaveNotes}
              disabled={!notes.trim() || isSubmittingNotes}
              className="px-4 py-2 text-white bg-[#5652B0] rounded-lg text-sm font-medium hover:bg-[#4A47A3] transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed ml-3 flex items-center gap-2">
              {isSubmittingNotes ? (
                <>
                  <CircularProgress size={16} color="inherit" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Save Notes
                </>
              )}
            </button>
          )}
        </DialogActions>
      </Dialog>

      {/* Settle Materials Modal */}
      <Dialog
        open={showSettleMaterialsModal}
        onClose={() => {
          if (!isSubmittingSettle) {
            setShowSettleMaterialsModal(false);
          }
        }}
        maxWidth="md"
        fullWidth>
        <DialogTitle className="flex items-center justify-between border-b p-4">
          <span className="text-lg font-semibold text-gray-900">
            Settle Materials
          </span>
          <IconButton
            onClick={() => setShowSettleMaterialsModal(false)}
            size="small"
            className="text-gray-500 hover:text-gray-700">
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent className="p-6">
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="bg-white border border-gray-100 rounded-lg p-5">
              <h3 className="text-base font-medium text-gray-800 mb-4">
                Basic Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {/* Settle Reason */}
                <FormControl fullWidth size="small">
                  <InputLabel>Settle Reason</InputLabel>
                  <Select
                    value={settleForm.settleReason}
                    onChange={e =>
                      handleSettleFormChange("settleReason", e.target.value)
                    }
                    label="Settle Reason">
                    <MenuItem value="damaged">Damaged</MenuItem>
                    <MenuItem value="lost">Lost</MenuItem>
                    <MenuItem value="wastage">Wastage</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>

                {/* Date and Time of Settle */}
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateTimePicker
                    label="Date and Time of Settle"
                    value={settleForm.settleDateTime}
                    onChange={newValue =>
                      handleSettleFormChange("settleDateTime", newValue)
                    }
                    slotProps={{
                      textField: {
                        size: "small",
                        InputProps: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <Clock size={18} className="text-[#5652B0]" />
                            </InputAdornment>
                          ),
                        },
                      },
                    }}
                  />
                </LocalizationProvider>

                {/* Settled By - Non-editable */}
                <TextField
                  label="Settled By"
                  size="small"
                  value={settleForm.settledBy}
                  disabled
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <User2 size={18} className="text-[#5652B0]" />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Settle Challan No */}
                <TextField
                  label="Settle Challan No."
                  size="small"
                  value={settleForm.settleChallanNo}
                  onChange={e =>
                    handleSettleFormChange("settleChallanNo", e.target.value)
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FileText size={18} className="text-[#5652B0]" />
                      </InputAdornment>
                    ),
                  }}
                />
              </div>

              {/* Notes */}
              <div className="mt-4">
                <TextField
                  label="Notes"
                  multiline
                  rows={3}
                  fullWidth
                  size="small"
                  value={settleForm.settleNote}
                  onChange={e =>
                    handleSettleFormChange("settleNote", e.target.value)
                  }
                  placeholder="Add any additional notes here..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <StickyNote
                          size={18}
                          className="text-[#5652B0] mt-1.5"
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </div>
            </div>

            {/* Materials Section */}
            <div className="space-y-4">
              <h3 className="text-base font-medium text-gray-800">Materials</h3>
              {jobslip.materials.map(material => (
                <div
                  key={material.id}
                  className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:border-[#5652B0]/30 transition-colors">
                  <div className="p-4 border-b border-gray-100 bg-gray-50/50">
                    <div className="flex items-start gap-4">
                      <div className="w-16 h-16 rounded-lg border border-gray-200 overflow-hidden bg-gray-50 flex-shrink-0">
                        {material.photo ? (
                          <img
                            src={material.photo}
                            alt={material.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            <ImageIcon size={20} />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-4">
                          <div>
                            <h3 className="text-base font-medium text-gray-800 mb-0.5">
                              {material.name}
                            </h3>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-500">
                                {material.unit}
                              </span>
                              <span className="text-gray-300">•</span>
                              <span className="text-sm text-gray-500">
                                {material.usedQty || 0}/
                                {material.totalQty || material.inStock} used
                              </span>
                            </div>
                          </div>
                          <div className="min-w-[180px]">
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              label="Total Settle Quantity"
                              value={settleQuantities[material.id]?.total || ""}
                              onChange={e =>
                                handleSettleQuantityChange(
                                  material.id,
                                  "total",
                                  e.target.value,
                                )
                              }
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    {material.unit}
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="px-4 py-3">
                    <div className="space-y-3">
                      {(Array.isArray(material.colors)
                        ? material.colors
                        : [material.color]
                      ).map(color => (
                        <div
                          key={color}
                          className="flex items-center justify-between gap-4">
                          <div className="flex items-center gap-4">
                            <div className="min-w-[120px] flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-700">
                                {typeof color === "string"
                                  ? color
                                  : color?.name || "N/A"}
                              </span>
                            </div>
                            <div className="min-w-[120px]">
                              <span className="text-sm text-gray-600">
                                {settleQuantities[material.id]?.[
                                  typeof color === "string"
                                    ? color
                                    : color?.name
                                ] || 0}
                                /{material.inStock} {material.unit}
                              </span>
                            </div>
                          </div>
                          <div className="min-w-[180px]">
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              label="Color Settle Quantity"
                              value={
                                settleQuantities[material.id]?.[
                                  typeof color === "string"
                                    ? color
                                    : color?.name
                                ] || ""
                              }
                              onChange={e =>
                                handleSettleQuantityChange(
                                  material.id,
                                  typeof color === "string"
                                    ? color
                                    : color?.name,
                                  e.target.value,
                                )
                              }
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    {material.unit}
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
        <DialogActions className="border-t p-4">
          <button
            onClick={() => setShowSettleMaterialsModal(false)}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">
            Cancel
          </button>
          <button
            onClick={handleSettleMaterials}
            disabled={isSubmittingSettle}
            className="px-4 py-2 text-white bg-[#5652B0] rounded-lg text-sm font-medium hover:bg-[#4A47A3] transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed ml-3 flex items-center gap-2">
            {isSubmittingSettle ? (
              <>
                <CircularProgress size={16} color="inherit" />
                Settling...
              </>
            ) : (
              "Settle"
            )}
          </button>
        </DialogActions>
      </Dialog>

      {/* Settle Design Modal */}
      <Dialog
        open={showSettleDesignModal}
        onClose={() => {
          if (!isSubmittingDesignSettle) {
            setShowSettleDesignModal(false);
          }
        }}
        maxWidth="md"
        fullWidth>
        <DialogTitle className="flex items-center justify-between border-b p-4">
          <span className="text-lg font-semibold text-gray-900">
            Settle Designs
          </span>
          <IconButton
            onClick={() => setShowSettleDesignModal(false)}
            size="small"
            className="text-gray-500 hover:text-gray-700">
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent className="p-6">
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="bg-white border border-gray-100 rounded-lg p-5">
              <h3 className="text-base font-medium text-gray-800 mb-4">
                Basic Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {/* Settle Reason */}
                <FormControl fullWidth size="small">
                  <InputLabel>Settle Reason</InputLabel>
                  <Select
                    value={settleDesignForm.settleReason}
                    onChange={e =>
                      handleSettleDesignFormChange(
                        "settleReason",
                        e.target.value,
                      )
                    }
                    label="Settle Reason">
                    <MenuItem value="damaged">Damaged</MenuItem>
                    <MenuItem value="lost">Lost</MenuItem>
                    <MenuItem value="wastage">Wastage</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>

                {/* Date and Time of Settle */}
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateTimePicker
                    label="Date and Time of Settle"
                    value={settleDesignForm.settleDateTime}
                    onChange={newValue =>
                      handleSettleDesignFormChange("settleDateTime", newValue)
                    }
                    slotProps={{
                      textField: {
                        size: "small",
                        InputProps: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <Clock size={18} className="text-[#5652B0]" />
                            </InputAdornment>
                          ),
                        },
                      },
                    }}
                  />
                </LocalizationProvider>

                {/* Settled By - Non-editable */}
                <TextField
                  label="Settled By"
                  size="small"
                  value={settleDesignForm.settledBy}
                  disabled
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <User2 size={18} className="text-[#5652B0]" />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Settle Challan No */}
                <TextField
                  label="Settle Challan No."
                  size="small"
                  value={settleDesignForm.settleChallanNo}
                  onChange={e =>
                    handleSettleDesignFormChange(
                      "settleChallanNo",
                      e.target.value,
                    )
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FileText size={18} className="text-[#5652B0]" />
                      </InputAdornment>
                    ),
                  }}
                />
              </div>

              {/* Notes */}
              <div className="mt-4">
                <TextField
                  label="Notes"
                  multiline
                  rows={3}
                  fullWidth
                  size="small"
                  value={settleDesignForm.settleNote}
                  onChange={e =>
                    handleSettleDesignFormChange("settleNote", e.target.value)
                  }
                  placeholder="Add any additional notes here..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <StickyNote
                          size={18}
                          className="text-[#5652B0] mt-1.5"
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </div>
            </div>

            {/* Designs Section */}
            <div className="space-y-4">
              <h3 className="text-base font-medium text-gray-800">Designs</h3>
              {jobslip.designs.map(design => (
                <div
                  key={design.id}
                  className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:border-[#5652B0]/30 transition-colors">
                  <div className="p-4 border-b border-gray-100 bg-gray-50/50">
                    <div className="flex items-start gap-4">
                      <div className="w-16 h-16 rounded-lg border border-gray-200 overflow-hidden bg-gray-50 flex-shrink-0">
                        {design.image ? (
                          <img
                            src={design.image}
                            alt={design.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            <ImageIcon size={20} />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-4">
                          <div>
                            <h3 className="text-base font-medium text-gray-800 mb-0.5">
                              {design.name}
                            </h3>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-500">
                                Design ID: {design.designId}
                              </span>
                            </div>
                          </div>
                          <div className="min-w-[180px]">
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              label="Total Settle Quantity"
                              value={
                                settleDesignQuantities[design.id]?.total || ""
                              }
                              onChange={e =>
                                handleSettleDesignQuantityChange(
                                  design.id,
                                  "total",
                                  "total",
                                  e.target.value,
                                )
                              }
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    pieces
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="px-4 py-3">
                    <div className="space-y-4">
                      {design.pieces.map((piece, pieceIndex) =>
                        piece.colors.map(color => (
                          <div
                            key={`${pieceIndex}-${color.name}`}
                            className="space-y-3">
                            <div className="flex items-center justify-between gap-4">
                              <div className="flex items-center gap-4">
                                <div className="min-w-[120px]">
                                  <span className="text-sm font-medium text-gray-700">
                                    {color.name}
                                  </span>
                                </div>
                                <div className="min-w-[120px]">
                                  <span className="text-sm text-gray-600">
                                    {settleDesignQuantities[design.id]?.[
                                      color.name
                                    ]?.total || 0}
                                    /{color.quantity} pieces
                                  </span>
                                </div>
                              </div>
                              <div className="min-w-[180px]">
                                <TextField
                                  fullWidth
                                  size="small"
                                  type="number"
                                  label="Color Settle Quantity"
                                  value={
                                    settleDesignQuantities[design.id]?.[
                                      color.name
                                    ]?.total || ""
                                  }
                                  onChange={e =>
                                    handleSettleDesignQuantityChange(
                                      design.id,
                                      color.name,
                                      "total",
                                      e.target.value,
                                    )
                                  }
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        pieces
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </div>
                            </div>
                            {/* Size Distribution */}
                            <div className="space-y-3 pl-8">
                              {color.sizes &&
                                Object.entries(color.sizes).map(
                                  ([size, qty]) => (
                                    <div
                                      key={size}
                                      className="flex items-center gap-4">
                                      <div className="min-w-[120px]">
                                        <span className="text-sm font-medium text-gray-700">
                                          Size {size}
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-4">
                                        <div className="min-w-[120px]">
                                          <span className="text-sm text-gray-600">
                                            {settleDesignQuantities[
                                              design.id
                                            ]?.[color.name]?.[size] || 0}
                                            /{qty} pieces
                                          </span>
                                        </div>
                                        <div className="min-w-[180px]">
                                          <TextField
                                            fullWidth
                                            size="small"
                                            type="number"
                                            label="Size Settle Quantity"
                                            value={
                                              settleDesignQuantities[
                                                design.id
                                              ]?.[color.name]?.[size] || ""
                                            }
                                            onChange={e =>
                                              handleSettleDesignQuantityChange(
                                                design.id,
                                                color.name,
                                                size,
                                                e.target.value,
                                              )
                                            }
                                            InputProps={{
                                              endAdornment: (
                                                <InputAdornment position="end">
                                                  pieces
                                                </InputAdornment>
                                              ),
                                            }}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  ),
                                )}
                            </div>
                          </div>
                        )),
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
        <DialogActions className="border-t p-4">
          <button
            onClick={() => setShowSettleDesignModal(false)}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">
            Cancel
          </button>
          <button
            onClick={handleSettleDesigns}
            disabled={isSubmittingDesignSettle}
            className="px-4 py-2 text-white bg-[#5652B0] rounded-lg text-sm font-medium hover:bg-[#4A47A3] transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed ml-3 flex items-center gap-2">
            {isSubmittingDesignSettle ? (
              <>
                <CircularProgress size={16} color="inherit" />
                Settling...
              </>
            ) : (
              "Settle"
            )}
          </button>
        </DialogActions>
      </Dialog>

      {/* Reminder Modal */}
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Dialog
          open={reminderModalOpen}
          onClose={() => {
            setReminderModalOpen(false);
            setSelectedReminder(null);
            setReminderDateTime(null);
            setReminderDescription("");
            setIsReminderViewMode(true);
          }}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            style: {
              borderRadius: "12px",
            },
          }}>
          <DialogTitle className="flex justify-between items-center border-b pb-4">
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-[#5652B0]" />
              <span className="text-xl font-semibold text-gray-800">
                {isReminderViewMode
                  ? "Reminders"
                  : selectedReminder
                  ? "Edit Reminder"
                  : "Set Reminder"}
              </span>
            </div>
            <button
              onClick={() => {
                setReminderModalOpen(false);
                setSelectedReminder(null);
                setReminderDateTime(null);
                setReminderDescription("");
                setIsReminderViewMode(true);
              }}
              className="text-gray-500 hover:text-gray-700 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </DialogTitle>
          <DialogContent>
            {isReminderViewMode ? (
              <div className="mt-6 flex flex-col gap-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-500 mb-1">Job Slip</div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-800">
                      {jobslip.id}
                    </span>
                    <span className="text-gray-400">•</span>
                    <span className="font-medium text-gray-800">
                      {jobslip.designs[0]?.name}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    Karigar:{" "}
                    <span className="text-gray-800">
                      {jobslip.karigar.name}
                    </span>
                  </div>
                </div>

                <div className="flex flex-col gap-3">
                  {reminders.length === 0 ? (
                    <div className="text-center py-6 text-gray-500">
                      No reminders set for this job slip
                    </div>
                  ) : (
                    reminders.map(reminder => (
                      <div
                        key={reminder.id}
                        className={`border rounded-lg p-4 ${
                          reminder.status === "completed"
                            ? "border-gray-200 bg-gray-50"
                            : "border-[#5652B0]/20"
                        }`}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Clock size={16} className="text-[#5652B0]" />
                              <span className="text-sm font-medium text-gray-900">
                                {reminder.dateTime.format(
                                  "DD MMM YYYY, hh:mm A",
                                )}
                              </span>
                              <span
                                className={`text-xs px-2 py-0.5 rounded ${
                                  reminder.status === "completed"
                                    ? "bg-green-100 text-green-700"
                                    : "bg-[#5652B0]/10 text-[#5652B0]"
                                }`}>
                                {reminder.status === "completed"
                                  ? "Completed"
                                  : "Pending"}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600">
                              {reminder.description}
                            </p>
                            <p className="text-xs text-gray-500 mt-2">
                              Set on {reminder.createdAt.format("DD MMM YYYY")}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() =>
                                handleToggleReminderStatus(reminder.id)
                              }
                              className={`p-2 rounded-lg transition-colors ${
                                reminder.status === "completed"
                                  ? "hover:bg-green-50 text-green-600"
                                  : "hover:bg-[#5652B0]/5 text-[#5652B0]"
                              }`}
                              title={
                                reminder.status === "completed"
                                  ? "Mark as Pending"
                                  : "Mark as Completed"
                              }>
                              <CheckCircle2 size={16} />
                            </button>
                            <button
                              onClick={() => handleEditReminder(reminder)}
                              className="p-2 rounded-lg hover:bg-[#5652B0]/5 text-[#5652B0] transition-colors"
                              title="Edit Reminder">
                              <Edit size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            ) : (
              <div className="mt-6 flex flex-col gap-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-500 mb-1">Job Slip</div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-800">
                      {jobslip.id}
                    </span>
                    <span className="text-gray-400">•</span>
                    <span className="font-medium text-gray-800">
                      {jobslip.designs[0]?.name}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    Karigar:{" "}
                    <span className="text-gray-800">
                      {jobslip.karigar.name}
                    </span>
                  </div>
                </div>

                <DateTimePicker
                  label="Reminder Date & Time"
                  value={reminderDateTime}
                  onChange={newValue => setReminderDateTime(newValue)}
                  className="w-full"
                  slotProps={{
                    textField: {
                      variant: "outlined",
                      fullWidth: true,
                      sx: {
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                          "&:hover fieldset": {
                            borderColor: "#5652B0",
                          },
                        },
                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                          {
                            borderColor: "#5652B0",
                          },
                      },
                    },
                  }}
                />

                <TextField
                  label="Reminder Description"
                  multiline
                  rows={3}
                  value={reminderDescription}
                  onChange={e => setReminderDescription(e.target.value)}
                  placeholder="Add a note about this reminder..."
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      "&:hover fieldset": {
                        borderColor: "#5652B0",
                      },
                    },
                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                      {
                        borderColor: "#5652B0",
                      },
                  }}
                />
              </div>
            )}
          </DialogContent>
          <DialogActions className="border-t p-4 gap-3">
            {isReminderViewMode ? (
              <>
                <Button
                  onClick={() => {
                    setReminderModalOpen(false);
                    setSelectedReminder(null);
                    setReminderDateTime(null);
                    setReminderDescription("");
                    setIsReminderViewMode(true);
                  }}
                  variant="outlined"
                  sx={{
                    borderColor: "#5652B0",
                    color: "#5652B0",
                    "&:hover": {
                      borderColor: "#4A47A3",
                      backgroundColor: "rgba(86, 82, 176, 0.04)",
                    },
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 500,
                  }}>
                  Close
                </Button>
                <Button
                  onClick={handleAddNewReminder}
                  variant="contained"
                  sx={{
                    bgcolor: "#5652B0",
                    "&:hover": {
                      bgcolor: "#4A47A3",
                    },
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 500,
                    boxShadow: "none",
                    "&:hover": {
                      bgcolor: "#4A47A3",
                      boxShadow: "none",
                    },
                  }}>
                  Add New Reminder
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={() => {
                    if (selectedReminder) {
                      setSelectedReminder(null);
                      setReminderDateTime(null);
                      setReminderDescription("");
                      setIsReminderViewMode(true);
                    } else {
                      setReminderModalOpen(false);
                      setSelectedReminder(null);
                      setReminderDateTime(null);
                      setReminderDescription("");
                      setIsReminderViewMode(true);
                    }
                  }}
                  variant="outlined"
                  sx={{
                    borderColor: "#5652B0",
                    color: "#5652B0",
                    "&:hover": {
                      borderColor: "#4A47A3",
                      backgroundColor: "rgba(86, 82, 176, 0.04)",
                    },
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 500,
                  }}>
                  {selectedReminder ? "Cancel Edit" : "Cancel"}
                </Button>
                <Button
                  onClick={handleSetReminder}
                  variant="contained"
                  disabled={!reminderDateTime}
                  sx={{
                    bgcolor: "#5652B0",
                    "&:hover": {
                      bgcolor: "#4A47A3",
                    },
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 500,
                    boxShadow: "none",
                    "&:hover": {
                      bgcolor: "#4A47A3",
                      boxShadow: "none",
                    },
                  }}>
                  {selectedReminder ? "Update Reminder" : "Set Reminder"}
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>
      </LocalizationProvider>
    </div>
  );
};

export default ViewJobslip;
